import React, { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Grid, TextField, Typography } from '@mui/material';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { useAppSnackbar } from '../../common/TableDesign';
import ModulePageHeader from '../../common/ModulePageHeader';
import { AccountPicker } from '../LedgerPickers';
import DocResultDialog from './DocResultDialog';
import { InventoryItemPicker, fmtDate, getErrorMessage, itemLabelOf } from './InventoryShared';

const emptyForm = () => ({
  item_id: '',
  quantity: 1,
  from_location: '',
  to_location: '',
  from_account_id: '',
  to_account_id: '',
  transfer_date: new Date().toISOString().slice(0, 10),
  reason: '',
});

// Stock transfers move ONE item between two stock GL accounts (verified
// against CreateStockTransferDto) - the quantity never changes, only where
// it sits. from/to_location are free-text labels (max 100 chars), not store
// records. Write-only like the other movement documents.
const InvTransfersTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.INVENTORY, ACTIONS.CREATE);
  const canApprove = can(RESOURCES.LEDGER.INVENTORY, ACTIONS.APPROVE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();

  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  if (!can(RESOURCES.LEDGER.INVENTORY, ACTIONS.READ)) {
    return <Alert severity="error">You do not have permission to view Stock Transfers.</Alert>;
  }

  const set = (key, value) => setValues((p) => ({ ...p, [key]: value }));
  const idOf = (d) => d?.transfer_id ?? d?.id;
  const numberOf = (d) => d?.transfer_number || (idOf(d) ? `STT-${idOf(d)}` : '');

  const submitCreate = async () => {
    if (!values.item_id) {
      showSnackbar('Item is required', 'error');
      return;
    }
    if (!(Number(values.quantity) > 0)) {
      showSnackbar('Quantity must be greater than zero', 'error');
      return;
    }
    if (!values.from_account_id || !values.to_account_id) {
      showSnackbar('Source and destination stock accounts are required', 'error');
      return;
    }
    if (String(values.from_account_id) === String(values.to_account_id)) {
      showSnackbar('Source and destination accounts must differ', 'error');
      return;
    }
    try {
      setSaving(true);
      const res = await ledgerAPI.createInvTransfer({
        item_id: Number(values.item_id),
        quantity: Number(values.quantity),
        ...(values.from_location?.trim() ? { from_location: values.from_location.trim() } : {}),
        ...(values.to_location?.trim() ? { to_location: values.to_location.trim() } : {}),
        from_account_id: Number(values.from_account_id),
        to_account_id: Number(values.to_account_id),
        transfer_date: values.transfer_date,
        ...(values.reason?.trim() ? { reason: values.reason.trim() } : {}),
      });
      const doc = res?.data?.data ?? res?.data ?? {};
      setResult({ ...doc, id: idOf(doc) ?? doc.transfer_id });
      setShowCreate(false);
      setValues(emptyForm());
      showSnackbar(`Transfer ${numberOf(doc)} created as DRAFT`, 'success');
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <ModulePageHeader
        title="Stock Transfers"
        subtitle="Move one item between two stock accounts - quantity never changes."
        actions={canCreate ? [
          <Button key="add" variant="contained" startIcon={<AddIcon />} onClick={() => { setValues(emptyForm()); setShowCreate(true); }}>
            New Stock Transfer
          </Button>,
        ] : null}
      />
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Transfers are tracked by document number (returned on creation). Approve then post to move the stock value.
          </Typography>
        </CardContent>
      </Card>

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Stock Transfer</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <InventoryItemPicker value={values.item_id} onChange={(id) => set('item_id', id)} required />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth size="small" type="number" label="Quantity" value={values.quantity}
                onChange={(e) => set('quantity', e.target.value)}
                inputProps={{ min: 0.01, step: 'any' }} required
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth size="small" type="date" label="Transfer Date" value={values.transfer_date}
                onChange={(e) => set('transfer_date', e.target.value)} InputLabelProps={{ shrink: true }} required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <AccountPicker label="From stock account" value={values.from_account_id} onChange={(id) => set('from_account_id', id)} required />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <AccountPicker label="To stock account" value={values.to_account_id} onChange={(id) => set('to_account_id', id)} required />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="From location (label)" value={values.from_location} onChange={(e) => set('from_location', e.target.value)} inputProps={{ maxLength: 100 }} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="To location (label)" value={values.to_location} onChange={(e) => set('to_location', e.target.value)} inputProps={{ maxLength: 100 }} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Reason" value={values.reason} onChange={(e) => set('reason', e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <DocResultDialog
        open={!!result}
        onClose={() => setResult(null)}
        title="Stock transfer"
        doc={result}
        setDoc={setResult}
        numberOf={numberOf}
        statusOf={(d) => d.status}
        api={{
          approve: (id) => ledgerAPI.approveInvTransfer(id),
          post: (id) => ledgerAPI.postInvTransfer(id),
          reverse: (id, d) => ledgerAPI.reverseInvTransfer(id, d),
        }}
        canApprove={canApprove}
        renderSummary={(d) => (
          <>
            <Typography variant="body2" color="text.secondary">
              {d.item ? itemLabelOf(d.item) : `Item ${d.item_id ?? ''}`} - qty {d.quantity} - {fmtDate(d.transfer_date)}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {d.from_location || '-'} -&gt; {d.to_location || '-'}
            </Typography>
            {d.amount != null && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Value moved: <strong>{formatAccountingAmount(d.amount)}</strong>
              </Typography>
            )}
          </>
        )}
      />

      {SnackbarComponent}
    </Box>
  );
};

export default InvTransfersTab;
